import { forwardRef, useEffect, useState } from 'react';
import { Input } from '@/components/atoms/input';
import { cn } from '@/lib/utils';

interface CurrencyInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange' | 'value' | 'inputMode'
> {
  value: number | '';
  onChange: (value: number | '') => void;
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ value, onChange, className, ...props }, ref) => {
    // Hold the raw text so partial decimals ("25.", "0.0") survive keystrokes.
    // Parsing to a number on every change would drop the trailing dot and make
    // cents impossible to type; we still emit the parsed number via onChange.
    const [draft, setDraft] = useState(value === '' ? '' : String(value));

    // Re-sync the visible text only when the numeric value changes from the
    // outside (reset, prefill). While typing, the draft is the source of truth,
    // so a value that already matches the draft leaves the raw text untouched.
    useEffect(() => {
      const parsed = draft === '' ? NaN : parseFloat(draft);
      const draftValue = Number.isNaN(parsed) ? '' : parsed;
      if (draftValue !== value) {
        setDraft(value === '' ? '' : String(value));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
      <div className="relative">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          $
        </span>
        <Input
          ref={ref}
          type="text"
          inputMode="decimal"
          value={draft}
          onChange={(e) => {
            // Keep only digits and a single decimal point.
            const cleaned = e.target.value.replace(/[^0-9.]/g, '');
            const parts = cleaned.split('.');
            const single = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : cleaned;
            // A leading dot implies a zero ("." → "0.", ".5" → "0.5") so the
            // draft never sits at a bare "." that reads as filled but parses to
            // nothing (which would sneak past a native `required` check).
            const next = single.startsWith('.') ? `0${single}` : single;
            setDraft(next);
            if (next === '') {
              onChange('');
              return;
            }
            const parsed = parseFloat(next);
            onChange(Number.isNaN(parsed) ? '' : parsed);
          }}
          className={cn('pl-6', className)}
          {...props}
        />
      </div>
    );
  },
);
CurrencyInput.displayName = 'CurrencyInput';
